import { BrowserRouter } from "react-router-dom";
import { ReactRouterAppProvider } from "@toolpad/core/react-router";
import { Chip, createTheme } from "@mui/material";
import { red } from "@mui/material/colors";

import UploadFileIcon from "@mui/icons-material/UploadFile";
import LocalMallIcon from "@mui/icons-material/LocalMall";
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import TimelineIcon from "@mui/icons-material/Timeline";
import WineBarIcon from "@mui/icons-material/WineBar";
import EditIcon from "@mui/icons-material/Edit";
import PriceChangeIcon from "@mui/icons-material/PriceChange";
import AddBoxIcon from "@mui/icons-material/AddBox";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import RuleIcon from '@mui/icons-material/Rule';
import InventoryIcon from '@mui/icons-material/Inventory';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import WidgetsIcon from '@mui/icons-material/Widgets';
import ListIcon from '@mui/icons-material/List';
import PeopleIcon from "@mui/icons-material/People";
import LiquorIcon from '@mui/icons-material/Liquor';
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import PlaceIcon from "@mui/icons-material/Place";
import ViewListIcon from "@mui/icons-material/ViewList";
import StraightenIcon from '@mui/icons-material/Straighten';
import CategoryIcon from '@mui/icons-material/Category';
import BackupIcon from '@mui/icons-material/Backup';
import StoreIcon from '@mui/icons-material/Store';

import useAuth from "../hooks/useAuth";
import { MODULES, ROLE_PERMISSIONS } from "../constants/permissions";

const customTheme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: "#ee5e18",
    },
    secondary: {
      main: "#ee5e18",
    },
    error: {
      main: red.A400,
    },
    background: {
      default: "#fff",
    },
  },
  typography: {
    fontFamily: ["Montserrat", "sans-serif"].join(","),
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          "& svg": {
            color: "#fff",
          },
          backgroundColor: "#ee5e18",
          color: "#fff",
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          color: "#fff",
        },
      },
    },
  },
});

export default function CustomRouter({ children }) {
  const auth = useAuth();
  const role = auth?.user?.role || null;
  const allowedModules = ROLE_PERMISSIONS[role] || [];

  const nav = [
    { kind: "header", title: "Products" },
    {
      segment: "product-creator",
      title: "Create Product",
      icon: <AddBoxIcon />,
      permissions: MODULES.PRODUCT_CREATOR,
    },
    {
      segment: "product-editor",
      title: "Edit Product",
      icon: <EditIcon />,
      permissions: MODULES.PRODUCT_EDITOR,
    },
    {
      segment: "bundles",
      title: "Bundles",
      icon: <WidgetsIcon />,
      permissions: MODULES.BUNDLES,
    },
    {
      segment: "bundles-change-log",
      title: "Bundles Changelog",
      icon: <ListIcon />,
      permissions: MODULES.BUNDLES_CHANGELOG,
    },
    {
      segment: "continue-selling-change-log",
      title: "Continue Selling Change Log",
      icon: <ListIcon />,
      permissions: MODULES.CONTINUE_SELLING,
    },
    {
      segment: "retail-price-tags",
      title: "Retail Price Tags",
      icon: <LocalOfferIcon />,
      permissions: MODULES.RETAIL_PRICE_TAGS,
    },

    { kind: "header", title: "Purchases" },
    {
      segment: "liquor-connect",
      title: "AGLC Price Changes",
      icon: <PriceChangeIcon />,
      permissions: MODULES.AGLC_PRICE_CHANGES,
    },
    {
      segment: "cost-update-csvs",
      title: "Cost update CSVs",
      icon: <PriceChangeIcon />,
      permissions: MODULES.COST_UPDATE_CSVS,
    },
    {
      segment: "aglc-purchase-orders",
      title: "AGLC Purchase Orders",
      icon: <RuleIcon />,
      permissions: MODULES.AGLC_PURCHASE_ORDERS,
    },
    {
      segment: "update-costs-cin7",
      title: "Update Costs (Cin7)",
      icon: <PriceChangeIcon />,
      permissions: MODULES.UPDATE_COSTS,
    },
    {
      segment: "receiving",
      title: "Receiving",
      icon: <InventoryIcon />,
      permissions: MODULES.RECEIVING,
    },
    { kind: "header", title: "Sales" },
    {
      segment: "ats-invoices",
      title: "ATS Cost Comparison",
      icon: <CompareArrowsIcon />,
      permissions: MODULES.ATS_COST_COMPARISON,
    },
    {
      segment: "fulfillment",
      title: "ATS Fulfillment & Tracking",
      icon: <UploadFileIcon />,
      action: <Chip label="NEW" color="primary" size="small" />,
      permissions: MODULES.ATS,
    },
    {
      segment: "orders",
      title: "Store Pickup Manager",
      icon: <LocalMallIcon />,
      permissions: MODULES.STORE_PICKUP,
    },
    { kind: "header", title: "Content" },
    {
      segment: "wine-chart",
      title: "Vintage Chart",
      icon: <TimelineIcon />,
      permissions: MODULES.VINTAGE_CHART,
    },
    {
      segment: "wine-tasting",
      title: "Tastings by ZYN",
      icon: <WineBarIcon />,
      permissions: MODULES.TASTINGS,
    },
    { kind: "header", title: "Management" },
    {
      segment: "users",
      title: "Users",
      icon: <PeopleIcon />,
      permissions: MODULES.USERS,
    },
    {
      segment: "product-types",
      title: "Product Types",
      icon: <AccountTreeIcon />,
      permissions: MODULES.PRODUCT_TYPES,
    },
    {
      segment: "locations",
      title: "Locations",
      icon: <PlaceIcon />,
      permissions: MODULES.LOCATIONS,
    },
    {
      segment: "units-of-measure",
      title: "Units of Measure",
      icon: <LiquorIcon />,
      permissions: MODULES.UNITS_OF_MEASURE,
    },
    {
      segment: "categories",
      title: "Categories",
      icon: <CategoryIcon />,
      permissions: MODULES.CATEGORIES,
    },
    {
      segment: "sizes",
      title: "Sizes",
      icon: <StraightenIcon />,
      permissions: MODULES.SIZES,
    },
    {
      segment: "presentations",
      title: "Presentations",
      icon: <ViewListIcon />,
      permissions: MODULES.PRESENTATIONS,
    },
    {
      segment: "container-options",
      title: "Container Options",
      icon: <Inventory2Icon />,
      permissions: MODULES.CONTAINER_OPTIONS,
    },
    {
      segment: "producers",
      title: "Producers",
      icon: <StoreIcon />,
      permissions: MODULES.PRODUCERS,
    },
    {
      segment: "shopify-backups",
      title: "Shopify Backups",
      icon: <BackupIcon />,
      permissions: MODULES.SHOPIFY_BACKUPS,
    },
  ];

  const filteredNavigation = nav.filter((item) => {
    if (!item.permissions) return true;
    return allowedModules.includes(item.permissions);
  });

  const cleanedNavigation = filteredNavigation.filter((item, i, arr) => {
  if (item.kind !== "header") return true;
    const nextItem = arr[i + 1];
    return Boolean(nextItem && nextItem.kind !== "header");
  });

  return (
    <BrowserRouter>
      <ReactRouterAppProvider
        theme={customTheme}
        navigation={cleanedNavigation}>
        {children}
      </ReactRouterAppProvider>
    </BrowserRouter>
  );
}
